import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../lib/cn.js';

const TILE_SIZE = 256;
// Google Static Maps caps the `size` param at 640x640 per dimension (scale=2
// then renders it at retina density).
const GOOGLE_MAX_DIMENSION = 640;

/**
 * Production static-map provider. When `VITE_GOOGLE_MAPS_STATIC_KEY` is set we
 * render a single image from Google's Maps Static API — a usage-based,
 * pay-per-request service (no flat monthly fee) that won't throttle production
 * traffic like the free community OSM tile server does.
 *
 * This key ships in the client bundle (it rides in the <img> URL), so it is
 * PUBLIC and MUST be a dedicated key restricted to the "Maps Static API" and
 * your domain(s) via HTTP-referrer restrictions in Google Cloud Console. Do NOT
 * reuse the server-side Places key.
 */
const GOOGLE_MAPS_STATIC_KEY = import.meta.env.VITE_GOOGLE_MAPS_STATIC_KEY as string | undefined;

interface StaticMapProps {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  zoom?: number;
  /** Fixed pixel height of the map. Width always fills the container. */
  height?: number;
  className?: string;
  /** Accessible label for the rendered map. */
  alt?: string;
  /** Render a labeled placeholder when coordinates are missing. */
  placeholderLabel?: string;
}

function lonToTileX(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * 2 ** zoom;
}

function latToTileY(lat: number, zoom: number): number {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom;
}

/**
 * Tiny static map thumbnail for a building location — no interactive SDK, no
 * iframe, just a single measured `<img>` area.
 *
 * Provider strategy:
 *  - If `VITE_GOOGLE_MAPS_STATIC_KEY` is configured we use Google's Maps Static
 *    API, a usage-based service that won't throttle production traffic.
 *  - Otherwise we fall back to stitching free OpenStreetMap raster tiles
 *    (`tile.openstreetmap.org`). Fine for local dev, but OSM's tile policy
 *    forbids heavy/commercial use, so keep a Google key set in production.
 *
 * When coordinates are missing we render an opinionated placeholder card so the
 * building-detail page still has a visual anchor in its address block.
 */
export function StaticMap({
  latitude,
  longitude,
  zoom = 16,
  height = 176,
  className,
  alt = 'Map of the building location',
  placeholderLabel = 'No coordinates yet',
}: StaticMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasCoords =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);

  if (!hasCoords) {
    return (
      <div
        className={cn(
          'flex w-full items-center justify-center rounded-lg border border-dashed border-border bg-surface-muted text-fg-muted',
          className,
        )}
        style={{ height }}
        role="img"
        aria-label={placeholderLabel}
      >
        <div className="flex flex-col items-center gap-2 text-sm">
          <MapPin className="h-6 w-6" aria-hidden />
          <span>{placeholderLabel}</span>
        </div>
      </div>
    );
  }

  const containerClass = cn(
    'relative w-full overflow-hidden rounded-lg border border-border bg-surface-muted',
    className,
  );

  // Preferred path: Google Maps Static API (usage-based, no throttling).
  if (GOOGLE_MAPS_STATIC_KEY && width > 0) {
    const imgWidth = Math.min(Math.round(width), GOOGLE_MAX_DIMENSION);
    const imgHeight = Math.min(Math.round(height), GOOGLE_MAX_DIMENSION);
    // Google expects lat,lng order. `scale=2` renders at retina density; the
    // %7C is a URL-encoded pipe separating the marker style from its location.
    const marker = `color:red%7C${latitude},${longitude}`;
    const src =
      `https://maps.googleapis.com/maps/api/staticmap` +
      `?center=${latitude},${longitude}&zoom=${zoom}&size=${imgWidth}x${imgHeight}` +
      `&scale=2&markers=${marker}&key=${GOOGLE_MAPS_STATIC_KEY}`;

    return (
      <div ref={containerRef} role="img" aria-label={alt} className={containerClass} style={{ height }}>
        <img
          src={src}
          alt=""
          width={imgWidth}
          height={imgHeight}
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  // Fallback: stitch free OpenStreetMap raster tiles.
  const tileCount = 2 ** zoom;
  const centerPxX = lonToTileX(longitude, zoom) * TILE_SIZE;
  const centerPxY = latToTileY(latitude, zoom) * TILE_SIZE;

  const tiles: Array<{ key: string; src: string; left: number; top: number }> = [];
  if (width > 0) {
    const originX = centerPxX - width / 2;
    const originY = centerPxY - height / 2;
    const minTileX = Math.floor(originX / TILE_SIZE);
    const maxTileX = Math.floor((originX + width) / TILE_SIZE);
    const minTileY = Math.floor(originY / TILE_SIZE);
    const maxTileY = Math.floor((originY + height) / TILE_SIZE);

    for (let tx = minTileX; tx <= maxTileX; tx += 1) {
      for (let ty = minTileY; ty <= maxTileY; ty += 1) {
        if (ty < 0 || ty >= tileCount) continue;
        const wrappedX = ((tx % tileCount) + tileCount) % tileCount;
        tiles.push({
          key: `${tx}-${ty}`,
          src: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${ty}.png`,
          left: tx * TILE_SIZE - originX,
          top: ty * TILE_SIZE - originY,
        });
      }
    }
  }

  return (
    <div ref={containerRef} role="img" aria-label={alt} className={containerClass} style={{ height }}>
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.src}
          alt=""
          width={TILE_SIZE}
          height={TILE_SIZE}
          loading="lazy"
          draggable={false}
          className="pointer-events-none absolute max-w-none select-none"
          style={{ left: tile.left, top: tile.top }}
        />
      ))}
      <MapPin
        className="pointer-events-none absolute h-7 w-7 -translate-x-1/2 -translate-y-full fill-danger text-danger drop-shadow"
        style={{ left: '50%', top: '50%' }}
        aria-hidden
      />
      <span className="pointer-events-none absolute bottom-0 right-0 rounded-tl bg-surface/80 px-1 text-[10px] leading-tight text-fg-muted">
        © OpenStreetMap
      </span>
    </div>
  );
}
